/**
 * Import Preview Component
 * 
 * Shows a preview of data to be imported with validation status.
 */

import { useMemo } from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface PreviewRow {
  data: Record<string, unknown>;
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ImportPreviewProps {
  rows: PreviewRow[];
  columns: { key: string; label: string }[];
  maxPreviewRows?: number;
}

// ============================================================================
// Component
// ============================================================================

export function ImportPreview({
  rows,
  columns,
  maxPreviewRows = 10,
}: ImportPreviewProps) {
  const previewRows = rows.slice(0, maxPreviewRows);
  const hasMore = rows.length > maxPreviewRows;
  
  const stats = useMemo(() => ({
    total: rows.length,
    valid: rows.filter(r => r.isValid).length,
    invalid: rows.filter(r => !r.isValid).length,
    withWarnings: rows.filter(r => r.warnings.length > 0).length,
  }), [rows]);
  
  const getRowStatusIcon = (row: PreviewRow) => {
    if (!row.isValid) {
      return <XCircle className="h-4 w-4 text-[var(--coral)]" />;
    }
    if (row.warnings.length > 0) {
      return <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />;
    }
    return <CheckCircle className="h-4 w-4 text-[var(--green)]" />;
  };
  
  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[var(--surface)] p-3 text-center">
          <div className="text-2xl font-bold text-[var(--text-pri)]">{stats.total}</div>
          <div className="text-xs text-[var(--text-sec)]">Total Rows</div>
        </div>
        <div className="bg-[var(--green-dim)] p-3 text-center">
          <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{stats.valid}</div>
          <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Valid</div>
        </div>
        <div className="bg-[var(--coral-dim)] p-3 text-center">
          <div className="text-2xl font-bold text-[var(--coral)]">{stats.invalid}</div>
          <div className="text-xs text-[var(--coral)]">Invalid</div>
        </div>
        <div className="bg-[var(--mustard-dim)] p-3 text-center">
          <div className="text-2xl font-bold text-[var(--mustard)]">{stats.withWarnings}</div>
          <div className="text-xs text-[var(--mustard)]">With Warnings</div>
        </div>
      </div>
      
      {/* Preview Table */}
      <div className="border border-[var(--rule)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[var(--rule)]">
            <thead className="bg-[var(--surface)]">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider w-10">
                  #
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider w-10">
                  Status
                </th>
                {columns.map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
              {previewRows.map((row, index) => (
                <tr
                  key={index}
                  className={row.isValid ? '' : 'bg-[var(--coral-dim)]'}
                >
                  <td className="px-3 py-2 text-sm text-[var(--text-ter)]">
                    {index + 1}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1" title={
                      row.errors.length > 0 
                        ? row.errors.join('; ') 
                        : row.warnings.length > 0 
                          ? row.warnings.join('; ')
                          : 'Valid'
                    }>
                      {getRowStatusIcon(row)}
                    </div>
                  </td>
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className="px-3 py-2 text-sm text-[var(--text-pri)] max-w-xs truncate"
                      title={String(row.data[col.key] ?? '')}
                    >
                      {String(row.data[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {hasMore && (
          <div className="px-3 py-2 bg-[var(--surface)] border-t border-[var(--rule)] text-sm text-[var(--text-sec)] text-center">
            Showing {maxPreviewRows} of {rows.length} rows
          </div>
        )}
      </div>
      
      {/* Error Details */}
      {stats.invalid > 0 && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4">
          <h4 className="text-sm font-medium text-[var(--coral)] mb-2">
            Validation Errors ({stats.invalid} row{stats.invalid !== 1 ? 's' : ''})
          </h4>
          <ul className="text-sm text-[var(--coral)] space-y-1 max-h-32 overflow-y-auto">
            {rows
              .filter(r => !r.isValid)
              .slice(0, 10)
              .map((row, idx) => (
                <li key={idx}>
                  Row {rows.indexOf(row) + 1}: {row.errors.join(', ')}
                </li>
              ))}
            {stats.invalid > 10 && (
              <li className="italic">...and {stats.invalid - 10} more errors</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ImportPreview;
